/**
 * Static help content for the searchable hub + section pages + the
 * per-page contextual `<HelpButton topic="..." />` side-sheet.
 *
 * Each topic has:
 *   • slug    – URL segment for /help/<slug>
 *   • title   – sentence-case heading
 *   • blurb   – one-line description shown on the hub & search results
 *   • body    – longer JSX-friendly content array (renders as <p> / <ol> / <code>)
 *   • icon    – lucide icon component (passed by reference, not rendered here)
 *   • keywords – extra strings the hub search matches against
 *
 * Content is intentionally Australian-HACCP-specific so it speaks the
 * same language as the rest of the app (FSANZ Standard 3.2.2A, mining-
 * camp use cases, GS1 labels).
 */
import {
  Thermometer, PackageOpen, Sparkles, Printer, ScanLine,
  GraduationCap, Bug, ShieldCheck, ClipboardCheck, Settings as SettingsIcon,
  WifiOff, AlertTriangle,
} from "lucide-react";

export const HELP_TOPICS = [
  {
    slug: "getting-started",
    title: "Getting started",
    blurb: "Set up your first site, add equipment, invite staff and print the first label in under 10 minutes.",
    icon: GraduationCap,
    keywords: ["onboarding", "setup", "first time", "welcome", "intro"],
    body: [
      { type: "p", text: "HACCP AU runs on three pillars: a site, the equipment in that site, and the people who log data against it. Configure them in this order and everything else (label printing, audit packs, supplier scorecards) lights up automatically." },
      { type: "h", text: "1. Create your sites" },
      { type: "p", text: "Open Settings → Sites and add every kitchen you run. Restaurants and mining camps live side-by-side; flip between them using the site selector at the top of every page." },
      { type: "h", text: "2. Add equipment" },
      { type: "p", text: "Each fridge, freezer, cool room and bain marie gets a row in Settings → Equipment. Set the target temperature range — that's how we decide if a reading is in-range or a breach." },
      { type: "h", text: "3. Invite staff" },
      { type: "p", text: "Settings → Users. Three roles: Admin (you), Staff (kitchen hands who log readings), Auditor (read-only)." },
      { type: "h", text: "4. Log your first reading" },
      { type: "p", text: "Open Temperature → New reading. Or print the equipment barcode from Settings → Equipment and let staff scan with the in-app camera (the PWA shortcut on a phone home screen makes this two taps)." },
    ],
  },
  {
    slug: "temperature",
    title: "Temperature logs",
    blurb: "Daily fridge / freezer / bain marie checks. Out-of-range readings auto-trigger a corrective-action prompt and a push notification.",
    icon: Thermometer,
    keywords: ["temp", "fridge", "freezer", "cold chain", "breach", "fsanz"],
    body: [
      { type: "p", text: "Each piece of equipment has a min/max range. Every reading is compared against that range and flagged in-range or breach in real time." },
      { type: "h", text: "Logging a reading" },
      { type: "ol", items: [
        "Open Temperature (or scan an equipment barcode from your phone camera).",
        "Pick the equipment.",
        "Type the °C value from your probe.",
        "Optional note (e.g. 'after delivery').",
        "Tap Save. Out-of-range readings show a red badge and open a corrective-action drawer.",
      ]},
      { type: "h", text: "Push notifications" },
      { type: "p", text: "Site managers who've installed the PWA and granted notifications get a phone alert within seconds of any breach — even when the app is closed. Configure under Settings → Notifications." },
    ],
  },
  {
    slug: "receival",
    title: "Receival (deliveries)",
    blurb: "Probe deliveries on arrival, accept or reject, attach a digital signature. Auto-builds the supplier scorecard.",
    icon: PackageOpen,
    keywords: ["delivery", "supplier", "probe", "rejection"],
    body: [
      { type: "p", text: "Open Receival → New delivery, pick the supplier, probe the cold/frozen items as they come off the truck, and accept or reject. Rejecting writes the reason into the supplier's scorecard so procurement can have a conversation." },
      { type: "h", text: "GS1 barcode scanning" },
      { type: "p", text: "If your supplier prints GS1-shaped labels (most do — look for AI(10) batch + AI(17) use-by), tap the scanner icon in the form and point your phone at the box. We'll auto-fill product, batch and use-by." },
    ],
  },
  {
    slug: "cleaning",
    title: "Cleaning schedule",
    blurb: "Daily / weekly / monthly sanitation sign-offs. Builds an audit-ready trail with timestamps and digital signatures.",
    icon: Sparkles,
    keywords: ["sanitation", "wipe down", "sign off", "schedule"],
    body: [
      { type: "p", text: "Cleaning tasks are defined per-site in Settings → Cleaning (e.g. 'Wipe down service line', 'Deep clean walk-in cool room weekly'). Each appears on the staff Cleaning page with a green Sign-off button." },
      { type: "p", text: "Sign-offs capture a digital signature pad, the user's name and a UTC timestamp. None of this is editable later — it's audit ledger material." },
    ],
  },
  {
    slug: "labels",
    title: "Food labels & thermal printing",
    blurb: "Print 80mm docket labels with prep date, use-by, batch, allergens and a GS1 QR. Direct LAN printing via the Kitchen Bridge.",
    icon: Printer,
    keywords: ["docket", "qr", "gs1", "thermal", "tablet", "label"],
    body: [
      { type: "p", text: "Label Printer auto-computes use-by from prep + shelf-life. The 80mm preview matches what the printer will emit — including the GS1-encoded QR that any phone camera can scan to land on the Verify Label page." },
      { type: "h", text: "Direct LAN printing" },
      { type: "p", text: "Install the Kitchen Bridge installer on the same LAN as your thermal printer (Settings → Kitchen Bridge). Labels then print over the LAN even when the cloud is unreachable — critical at mining camps with patchy WAN." },
      { type: "h", text: "Verify a printed label later" },
      { type: "p", text: "Any phone camera scanning a printed label QR lands on /labels/verify and shows the audit trail: prep details + any temperature breaches recorded at that site since the label was printed." },
    ],
  },
  {
    slug: "audit",
    title: "Audit pack PDFs",
    blurb: "One-click PDF export covering every workflow — bring this to your next FSANZ audit.",
    icon: ShieldCheck,
    keywords: ["pdf", "audit pack", "fsanz", "export", "compliance"],
    body: [
      { type: "p", text: "Each workflow page has a 'Download PDF' button — pick a date range and we'll render a styled, paginated PDF on the server with your site name, logo and signatures." },
      { type: "p", text: "Auditor accounts (Settings → Users → role: Auditor) get read-only access to every workflow so you can hand them a login during the audit and they can browse without risk of accidental edits." },
    ],
  },
  {
    slug: "scan",
    title: "Camera scanning & QuickScan",
    blurb: "Use your phone camera to scan equipment barcodes or food-label QR codes — works on iPhone Safari and Android Chrome.",
    icon: ScanLine,
    keywords: ["camera", "qr", "barcode", "ios", "android", "html5-qrcode"],
    body: [
      { type: "p", text: "Tap Scan in the sidebar (or the 'Scan & log' PWA shortcut on your home screen). Grant camera permission once — the app asks for environment-facing camera on phones so you don't see a selfie." },
      { type: "h", text: "What it scans" },
      { type: "ol", items: [
        "Equipment barcodes (Code128) — auto-opens the temperature form for that fridge/freezer.",
        "GS1 food labels — auto-opens Verify Label for that batch.",
        "HACCP AU verify URLs — opens the audit-trail page in 1 tap.",
      ]},
      { type: "h", text: "iPhone tips" },
      { type: "p", text: "Safari needs the page to be served over HTTPS to grant camera access. The HACCP AU preview and production URLs both meet this; localhost development needs a tunnelled HTTPS or one of the Chrome flags." },
    ],
  },
  {
    slug: "training",
    title: "Training mode",
    blurb: "Practise the workflow without writing to the real audit ledger — perfect for onboarding new staff.",
    icon: GraduationCap,
    keywords: ["sandbox", "practice", "onboard", "new staff", "rehearsal"],
    body: [
      { type: "p", text: "Flip Training Mode on from the topbar. A yellow banner appears, and every mutation (logs, labels, sign-offs) is diverted to a sandbox so nothing reaches the real audit log. Reads still show real data so the UI looks like a normal shift." },
      { type: "p", text: "When the new hire is ready, flip Training Mode off — the next save lands in the real ledger and the practice rows are forgotten." },
      { type: "p", text: "Tip: pair training mode with the first-time walkthrough (re-trigger from this page) to give new staff a guided tour of the dashboard." },
    ],
  },
  {
    slug: "pest",
    title: "Pest control logs",
    blurb: "Sightings, bait-station checks, exterminator visits — keeps your FSANZ pest-management plan up to date.",
    icon: Bug,
    keywords: ["pest", "rodent", "fly", "bait", "extermination"],
    body: [
      { type: "p", text: "Open Pest Control → New log. Tag the area, type (rodent / fly / cockroach / other), action taken, and attach a digital signature." },
    ],
  },
  {
    slug: "checklists",
    title: "Checklists",
    blurb: "Opening / closing / shift-handover templates. Tablet-friendly tick-the-box format with mandatory signatures.",
    icon: ClipboardCheck,
    keywords: ["checklist", "opening", "closing", "handover"],
    body: [
      { type: "p", text: "Settings → Checklists lets you define templates. Staff open Checklists, pick a template, tap each item as they go, sign off at the end. The completed run is timestamped and read-only." },
    ],
  },
  {
    slug: "offline",
    title: "Offline mode",
    blurb: "The whole app works without internet. Writes queue locally and sync the moment WAN comes back — critical at mining camps.",
    icon: WifiOff,
    keywords: ["offline", "no internet", "mining camp", "remote", "sync", "outbox"],
    body: [
      { type: "p", text: "Install HACCP AU as a PWA (Settings → Install). Once installed, the service worker caches the app shell and last 30 days of read data. Every write is queued to an IndexedDB outbox if the network is down." },
      { type: "p", text: "When connectivity returns, the queued rows flush automatically — Background Sync API fires the moment the radio is back, even if the app is closed. A green 'Synced' pill in the topbar confirms everything is up to date." },
    ],
  },
  {
    slug: "troubleshooting",
    title: "Troubleshooting",
    blurb: "Printer offline, scanner won't open, push notifications silent — start here.",
    icon: AlertTriangle,
    keywords: ["printer", "broken", "fix", "help", "trouble", "issue"],
    body: [
      { type: "h", text: "Printer not printing" },
      { type: "ol", items: [
        "Settings → Sites → check the Printer IP for the active site (default is 192.168.1.50:9100).",
        "Ping it from a laptop on the same LAN to confirm it's on the network.",
        "Settings → Kitchen Bridge → Test — green means the bridge can reach the printer.",
      ]},
      { type: "h", text: "Camera scanner won't open" },
      { type: "ol", items: [
        "Permission denied? Browser settings → Site permissions → Camera → Allow.",
        "iPhone Safari requires HTTPS — preview and prod URLs both meet this.",
        "If the camera opens but doesn't read codes, try better lighting or move the phone 15-20 cm from the code.",
      ]},
      { type: "h", text: "Push notifications silent" },
      { type: "ol", items: [
        "Settings → Notifications → Enable — accept the browser prompt.",
        "iOS 16.4+ required for push, and the PWA must be installed to the home screen first.",
        "Test alert button confirms end-to-end delivery.",
      ]},
    ],
  },
  {
    slug: "settings",
    title: "Settings reference",
    blurb: "Every toggle and field, what it does, and who's allowed to change it.",
    icon: SettingsIcon,
    keywords: ["settings", "config", "preferences", "admin"],
    body: [
      { type: "p", text: "Admins only — staff and auditors see a read-only message instead." },
      { type: "h", text: "Sites" },
      { type: "p", text: "Add, rename, delete sites. Configure the network thermal printer per site (IP + port). Toggle 'Publish public trust page' to expose a marketing-friendly compliance dashboard at /trust/<id>." },
      { type: "h", text: "Equipment barcodes" },
      { type: "p", text: "Each row has a short, human-readable barcode ID printed as Code128 onto a sticker. Edit + Print buttons live next to each barcode." },
    ],
  },
];

/** Lookup helper for the section page + contextual help button. */
export function getHelpTopic(slug) {
  return HELP_TOPICS.find((t) => t.slug === slug) || null;
}

/** Simple substring search across title / blurb / keywords / body text. */
export function searchHelp(query) {
  const q = (query || "").trim().toLowerCase();
  if (!q) return HELP_TOPICS;
  return HELP_TOPICS.filter((t) => {
    const hay = [
      t.title,
      t.blurb,
      ...(t.keywords || []),
      ...(t.body || []).map((b) =>
        b.text || (Array.isArray(b.items) ? b.items.join(" ") : "")
      ),
    ].join(" ").toLowerCase();
    return hay.includes(q);
  });
}
